
import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Interfaces
interface Sale {
  id: string;
  date: string;
  automatId: string;
  automatName: string;
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  profit: number;
}

interface FixedCost {
  id: string;
  automatId?: string;
  automatName?: string;
  name: string;
  category: string;
  costType: string;
  amount?: number;
  interval: string;
  startDate?: string;
  endDate?: string;
  description?: string;
  isRevenue?: boolean;
  revenuePercentage?: number;
  revenueAmount?: number;
  notes?: string;
  status: string;
}

interface ManualPayment {
  id: string;
  date: string;
  type: 'income' | 'expense';
  amount: number;
  description: string;
}

interface MonthlyData {
  month: string;
  revenue: number;
  costs: number;
  fixedCosts: number;
  fixedRevenue: number;
  manualPayments: number;
  grossProfit: number;
  netProfit: number;
  netMargin: number;
  isCurrentMonth: boolean;
  isFutureMonth: boolean;
  isPastMonth: boolean;
}

interface Automat {
  id: string;
  name: string;
  location: string;
}

export default function Budgetierung() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Automatische Monatserkennung - wird jeden Tag aktualisiert
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // States
  const [sales, setSales] = useState<Sale[]>([]);
  const [fixedCosts, setFixedCosts] = useState<FixedCost[]>([]);
  const [manualPayments, setManualPayments] = useState<ManualPayment[]>([]);
  const [automaten, setAutomaten] = useState<Automat[]>([]);

  // Automatische Aktualisierung des aktuellen Monats
  useEffect(() => {
    const updateCurrentMonth = () => {
      const now = new Date();
      const newCurrentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      
      if (newCurrentMonth !== currentMonth) {
        console.log(`🔄 Automatischer Monatswechsel erkannt: ${currentMonth} → ${newCurrentMonth}`);
        setCurrentMonth(newCurrentMonth);
      }
    };

    // Prüfe sofort beim Laden
    updateCurrentMonth();

    // Prüfe jeden Tag um Mitternacht (alle 60 Sekunden für Demo-Zwecke)
    const interval = setInterval(updateCurrentMonth, 60000);

    // Cleanup
    return () => clearInterval(interval);
  }, [currentMonth]);

  // Load data from Supabase
  useEffect(() => {
    if (user) {
      loadAllData();
    }
  }, [user]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 LOADING ALL DATA FOR BUDGETIERUNG...');

      // Load sales
      const { data: salesData, error: salesError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'sale');

      if (salesError) {
        console.error('❌ Error loading sales:', salesError);
        throw salesError;
      }

      const salesList = (salesData || []).map(item => {
        try {
          const saleData = item.data || {};
          return {
            id: item.id,
            date: saleData.date || new Date().toISOString().split('T')[0],
            automatId: saleData.automatId || '',
            automatName: saleData.automatName || 'Unbekannt',
            productId: saleData.productId || '',
            productName: saleData.productName || 'Unbekannt',
            quantity: Number(saleData.quantity) || 0,
            unitPrice: Number(saleData.unitPrice) || 0,
            totalPrice: Number(saleData.totalPrice) || 0,
            profit: Number(saleData.profit) || 0
          };
        } catch (err) {
          console.warn('⚠️ Fehler beim Verarbeiten von Sale:', item.id, err);
          return null;
        }
      }).filter(Boolean) as Sale[];

      setSales(salesList);
      console.log('📊 Loaded sales:', salesList.length);

      // Load automaten
      const { data: automatenData, error: automatenError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'automat');

      if (automatenError) {
        console.error('❌ Error loading automaten:', automatenError);
        throw automatenError;
      }

      const automatenList = (automatenData || [])
        .map(item => {
          try {
            const automatData = item.data || {};
            return {
              id: item.id,
              name: automatData.name || 'Unbekannter Automat',
              location: automatData.location || 'Unbekannter Standort',
              machine_type: automatData.machine_type
            };
          } catch (err) {
            console.warn('⚠️ Fehler beim Verarbeiten von Automat:', item.id, err);
            return null;
          }
        })
        .filter(item => item && (item.machine_type === 'physical_automat' || !item.machine_type))
        .map(item => ({
          id: item!.id,
          name: item!.name,
          location: item!.location
        }));
      
      console.log('🤖 Loaded automaten for Budgetierung:', automatenList.length);
      setAutomaten(automatenList);

      // Load fixed costs from fixkosten table
      const { data: fixedCostsData, error: fixedCostsError } = await supabase
        .from('fixkosten')
        .select('*')
        .eq('user_id', user?.id);

      if (fixedCostsError) {
        console.error('❌ Error loading fixed costs:', fixedCostsError);
        throw fixedCostsError;
      }

      const fixedCostsList = (fixedCostsData || []).map(item => {
        try {
          return {
            id: item.id,
            automatId: item.automat_id,
            automatName: item.automat_name,
            name: item.name || 'Unbenannt',
            category: item.category || 'Sonstige',
            costType: item.cost_type || 'fixkosten',
            amount: typeof item.amount === 'number' ? item.amount : (parseFloat(item.amount) || 0),
            interval: item.interval || 'monatlich',
            startDate: item.start_date,
            endDate: item.end_date,
            description: item.description,
            isRevenue: item.is_revenue === true,
            revenuePercentage: typeof item.revenue_percentage === 'number' ? item.revenue_percentage : (parseFloat(item.revenue_percentage) || 0),
            revenueAmount: typeof item.revenue_amount === 'number' ? item.revenue_amount : (parseFloat(item.revenue_amount) || 0),
            notes: item.notes,
            status: item.status || 'aktiv'
          };
        } catch (err) {
          console.warn('⚠️ Fehler beim Verarbeiten von Fixkosten:', item.id, err);
          return null;
        }
      }).filter(Boolean) as FixedCost[];

      setFixedCosts(fixedCostsList);
      console.log('💰 Loaded fixed costs:', fixedCostsList.length);

      // Load manual payments
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'manual_payment');

      if (paymentsError) {
        console.error('❌ Error loading manual payments:', paymentsError);
        throw paymentsError;
      }

      const paymentsList = (paymentsData || []).map(item => {
        try {
          const paymentData = item.data || {};
          return {
            id: item.id,
            date: paymentData.date || new Date().toISOString().split('T')[0],
            type: paymentData.type === 'income' ? 'income' : 'expense',
            amount: Number(paymentData.amount) || 0,
            description: paymentData.description || 'Keine Beschreibung'
          };
        } catch (err) {
          console.warn('⚠️ Fehler beim Verarbeiten von Payment:', item.id, err);
          return null;
        }
      }).filter(Boolean) as ManualPayment[];

      setManualPayments(paymentsList);
      console.log('💳 Loaded manual payments:', paymentsList.length);

    } catch (error) {
      console.error('❌ Error loading data:', error);
      setError('Fehler beim Laden der Daten. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
      console.log('✅ Loading complete');
    }
  };

  // Sichere Währungsformatierung
  const formatCurrency = (amount: number) => {
    if (typeof amount !== 'number' || isNaN(amount) || !isFinite(amount)) {
      return 'CHF 0.00';
    }
    return `CHF ${amount.toFixed(2)}`;
  };

  // Berechne Monatsdaten für ein bestimmtes Jahr und Monat
  const calculateMonthlyData = (year: number, month: number): MonthlyData => {
    const monthStr = `${year}-${String(month).padStart(2, '0')}`;
    
    // Filter sales for this month
    const monthlySales = sales.filter(sale => {
      try {
        const saleDate = new Date(sale.date);
        const saleYear = saleDate.getFullYear();
        const saleMonth = saleDate.getMonth() + 1;
        return saleYear === year && saleMonth === month;
      } catch (err) {
        console.warn('⚠️ Ungültiges Verkaufsdatum:', sale.date);
        return false;
      }
    });

    // Filter manual payments for this month
    const monthlyManualPayments = manualPayments.filter(payment => {
      try {
        const paymentDate = new Date(payment.date);
        return paymentDate.getFullYear() === year && paymentDate.getMonth() + 1 === month;
      } catch (err) {
        console.warn('⚠️ Ungültiges Zahlungsdatum:', payment.date);
        return false;
      }
    });

    const revenue = monthlySales.reduce((sum, sale) => sum + (Number(sale.totalPrice) || 0), 0);
    const costs = monthlySales.reduce((sum, sale) => sum + ((Number(sale.totalPrice) || 0) - (Number(sale.profit) || 0)), 0);
    const grossProfit = revenue - costs;

    // Calculate fixed costs and revenue
    let totalFixedCosts = 0;
    let totalFixedRevenue = 0;

    fixedCosts.forEach(cost => {
      if (cost.status !== 'aktiv') return;

      // Date validation
      if (cost.startDate) {
        const startDate = new Date(cost.startDate);
        const costStartYear = startDate.getFullYear();
        const costStartMonth = startDate.getMonth() + 1;
        if (costStartYear > year || (costStartYear === year && costStartMonth > month)) {
          return;
        }
      }

      if (cost.endDate) {
        const endDate = new Date(cost.endDate);
        const costEndYear = endDate.getFullYear();
        const costEndMonth = endDate.getMonth() + 1;
        if (costEndYear < year || (costEndYear === year && costEndMonth < month)) {
          return;
        }
      }

      let monthlyAmount = 0;

      // Base amount calculation
      if (cost.amount && cost.amount > 0) {
        if (cost.interval === 'monatlich') {
          monthlyAmount = cost.amount;
        } else if (cost.interval === 'jährlich') {
          monthlyAmount = cost.amount / 12;
        } else if (cost.interval === 'einmalig') {
          if (cost.startDate) {
            const startDate = new Date(cost.startDate);
            if (startDate.getFullYear() === year && startDate.getMonth() + 1 === month) {
              monthlyAmount = cost.amount;
            }
          } else {
            monthlyAmount = cost.amount;
          }
        }
      }

      // Revenue percentage calculation
      if (cost.revenuePercentage && cost.revenuePercentage > 0) {
        let relevantRevenue = 0;

        if (cost.automatId) {
          relevantRevenue = monthlySales
            .filter(sale => sale.automatId === cost.automatId)
            .reduce((sum, sale) => sum + (sale.totalPrice || 0), 0);
        } else {
          relevantRevenue = monthlySales.reduce((sum, sale) => sum + (sale.totalPrice || 0), 0);
        }

        const revenueShareAmount = (relevantRevenue * cost.revenuePercentage) / 100;
        monthlyAmount += revenueShareAmount;
      }

      if (cost.revenueAmount && cost.revenueAmount > 0) {
        monthlyAmount += cost.revenueAmount;
      }

      if (cost.isRevenue || cost.costType === 'fixkosten-einnahme') {
        totalFixedRevenue += monthlyAmount;
      } else {
        totalFixedCosts += monthlyAmount;
      }
    });

    const manualIncome = monthlyManualPayments
      .filter(p => p.type === 'income')
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    
    const manualExpenses = monthlyManualPayments
      .filter(p => p.type === 'expense')
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    const manualPaymentsNet = manualIncome - manualExpenses;

    const netProfit = grossProfit + totalFixedRevenue - totalFixedCosts + manualPaymentsNet;
    const netMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;

    const currentMonthNumber = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    return {
      month: monthStr,
      revenue,
      costs,
      fixedCosts: totalFixedCosts,
      fixedRevenue: totalFixedRevenue,
      manualPayments: manualPaymentsNet,
      grossProfit,
      netProfit,
      netMargin,
      isCurrentMonth: year === currentYear && month === currentMonthNumber,
      isFutureMonth: year > currentYear || (year === currentYear && month > currentMonthNumber),
      isPastMonth: year < currentYear || (year === currentYear && month < currentMonthNumber)
    };
  };

  // Berechne Automaten-spezifische Daten für ein bestimmtes Jahr und Monat
  const calculateAutomatMonthlyData = (automat: Automat, year: number, month: number) => {
    // Filter sales for this month and automat
    const automatMonthlySales = sales.filter(sale => {
      try {
        const saleDate = new Date(sale.date);
        const saleYear = saleDate.getFullYear();
        const saleMonth = saleDate.getMonth() + 1;
        return saleYear === year && saleMonth === month && sale.automatId === automat.id;
      } catch (err) {
        return false;
      }
    });

    const automatRevenue = automatMonthlySales.reduce((sum, sale) => sum + (sale.totalPrice || 0), 0);
    const automatCosts = automatMonthlySales.reduce((sum, sale) => sum + ((sale.totalPrice || 0) - (sale.profit || 0)), 0);
    const automatGrossProfit = automatRevenue - automatCosts;

    // Get all sales for this month (for percentage calculations)
    const allMonthlySales = sales.filter(sale => {
      try {
        const saleDate = new Date(sale.date);
        return saleDate.getFullYear() === year && saleDate.getMonth() + 1 === month;
      } catch (err) {
        return false;
      }
    });

    const totalAutomatenRevenue = allMonthlySales.reduce((sum, sale) => sum + (sale.totalPrice || 0), 0);
    const automatRevenueShareRatio = totalAutomatenRevenue > 0 ? automatRevenue / totalAutomatenRevenue : 1 / automaten.length;

    let automatFixedCosts = 0;
    let automatFixedRevenue = 0;
    let automatRevenueShare = 0;

    fixedCosts.forEach(cost => {
      if (cost.status !== 'aktiv') return;

      // Date validation
      if (cost.startDate) {
        const startDate = new Date(cost.startDate);
        const costStartYear = startDate.getFullYear();
        const costStartMonth = startDate.getMonth() + 1;
        if (costStartYear > year || (costStartYear === year && costStartMonth > month)) {
          return;
        }
      }

      if (cost.endDate) {
        const endDate = new Date(cost.endDate);
        const costEndYear = endDate.getFullYear();
        const costEndMonth = endDate.getMonth() + 1;
        if (costEndYear < year || (costEndYear === year && costEndMonth < month)) {
          return;
        }
      }

      let monthlyAmount = 0;

      // Base amount calculation
      if (cost.amount && cost.amount > 0) {
        if (cost.interval === 'monatlich') {
          monthlyAmount = cost.amount;
        } else if (cost.interval === 'jährlich') {
          monthlyAmount = cost.amount / 12;
        } else if (cost.interval === 'einmalig') {
          if (cost.startDate) {
            const startDate = new Date(cost.startDate);
            if (startDate.getFullYear() === year && startDate.getMonth() + 1 === month) {
              monthlyAmount = cost.amount;
            }
          } else {
            monthlyAmount = cost.amount;
          }
        }
      }

      // Revenue percentage calculation
      if (cost.revenuePercentage && cost.revenuePercentage > 0) {
        let relevantRevenue = 0;

        if (cost.automatId === automat.id) {
          relevantRevenue = automatRevenue;
        } else if (!cost.automatId) {
          relevantRevenue = automatRevenue; // Apply to this automat's revenue
        }
        
        if (relevantRevenue > 0) {
          const revenueShareAmount = (relevantRevenue * cost.revenuePercentage) / 100;
          automatRevenueShare += revenueShareAmount;
        }
      }

      if (cost.revenueAmount && cost.revenueAmount > 0) {
        if (cost.automatId === automat.id) {
          monthlyAmount += cost.revenueAmount;
        } else if (!cost.automatId) {
          monthlyAmount += cost.revenueAmount * automatRevenueShareRatio;
        }
      }

      // Apply costs based on automat assignment
      if (cost.automatId === automat.id) {
        // Direct assignment to this automat
        if (cost.isRevenue || cost.costType === 'fixkosten-einnahme') {
          automatFixedRevenue += monthlyAmount;
        } else {
          automatFixedCosts += monthlyAmount;
        }
      } else if (!cost.automatId) {
        // General costs - distribute proportionally
        const proportionalAmount = monthlyAmount * automatRevenueShareRatio;
        if (cost.isRevenue || cost.costType === 'fixkosten-einnahme') {
          automatFixedRevenue += proportionalAmount;
        } else {
          automatFixedCosts += proportionalAmount;
        }
      }
    });

    const automatNetProfit = automatGrossProfit + automatFixedRevenue - automatFixedCosts - automatRevenueShare;
    const netMargin = automatRevenue > 0 ? (automatNetProfit / automatRevenue) * 100 : 0;

    const currentMonthNumber = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    return {
      month: month,
      revenue: automatRevenue,
      costs: automatCosts,
      fixedRevenue: automatFixedRevenue,
      fixedCosts: automatFixedCosts,
      revenueShare: automatRevenueShare,
      netProfit: automatNetProfit,
      netMargin,
      isCurrentMonth: year === currentYear && month === currentMonthNumber,
      isFutureMonth: year > currentYear || (year === currentYear && month > currentMonthNumber),
      isPastMonth: year < currentYear || (year === currentYear && month < currentMonthNumber)
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Lade Budgetierungsdaten...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-red-600 mb-4"></i>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadAllData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="bg-white rounded-lg shadow-sm border w-full overflow-x-hidden">
          {/* Header */}
          <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                  <i className="ri-calendar-check-line mr-3 text-blue-600"></i>
                  Budget-Übersicht
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Monatliche Finanzübersicht mit automatischer Monatserkennung und detaillierter Aufschlüsselung
                </p>
              </div>
              <button
                onClick={() => navigate('/finanzen')}
                className="w-full sm:w-auto px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Zurück zu Finanzen
              </button>
            </div>
          </div>

          <div className="p-3 sm:p-4 lg:p-6 w-full overflow-x-hidden">
            {/* Datenübersicht */}
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <i className="ri-database-2-line text-green-600 text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                <div className="min-w-0">
                  <h4 className="text-sm font-medium text-green-900 mb-1">Datenübersicht</h4>
                  <div className="text-sm text-green-800 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>📊 Verkäufe: <strong>{sales.length}</strong></div>
                    <div>💰 Fixkosten: <strong>{fixedCosts.length}</strong></div>
                    <div>💳 Zahlungen: <strong>{manualPayments.length}</strong></div>
                    <div>🤖 Automaten: <strong>{automaten.length}</strong></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Jahresauswahl */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jahr auswählen:
              </label>
              <select
                value={new Date().getFullYear()}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                {(() => {
                  const years = [];
                  const currentYear = new Date().getFullYear();
                  for (let i = currentYear - 2; i <= currentYear + 1; i++) {
                    years.push(i);
                  }
                  return years.map(year => (
                    <option key={year} value={year}>{year}</option> 
                  ));
                })()}
              </select>
            </div>

            {/* Automatische Monatserkennung Status */}
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <i className="ri-calendar-check-line text-green-600 text-xl mr-3 mt-0.5 flex-shrink-0"></i>
                <div className="min-w-0">
                  <h4 className="text-sm font-medium text-green-900 mb-1">Automatische Monatserkennung aktiv</h4>
                  <p className="text-sm text-green-800">
                    Aktueller Monat: <strong>{(() => {
                      const monthNumber = parseInt(currentMonth.split('-')[1]);
                      const year = parseInt(currentMonth.split('-')[0]);
                      const monthNames = [
                        'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
                      ];
                      return `${monthNames[monthNumber - 1]} ${year}`;
                    })()}</strong> - Das System erkennt automatisch Monatswechsel und übernimmt alle Daten aus der Finanz-Übersicht
                  </p>
                </div>
              </div>
            </div>

            {/* Budget-Tabelle */}
            <div className="bg-white border rounded-lg overflow-hidden mb-8">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900">Monatliche Budget-Übersicht {new Date().getFullYear()}</h3>
                <p className="text-sm text-gray-600 mt-1">Detaillierte Aufschlüsselung aller Finanzen pro Monat - automatisch aktualisiert</p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monat</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Umsatz</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Wareneinsatz</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Fixe Einnahmen</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Fixkosten</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Manuelle Zahlungen</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Nettogewinn</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Marge</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(() => {
                      const currentYear = new Date().getFullYear();
                      const monthlyResults = [];

                      // Generate data for all 12 months
                      for (let month = 1; month <= 12; month++) {
                        const monthData = calculateMonthlyData(currentYear, month);
                        monthlyResults.push(monthData);
                      }

                      return monthlyResults.map((monthData, index) => (
                        <tr key={monthData.month} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${monthData.isCurrentMonth ? 'bg-blue-50 border-blue-200' : ''} ${monthData.isFutureMonth ? 'opacity-60' : ''} ${monthData.isPastMonth ? 'bg-gray-100' : ''}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className={`w-3 h-3 rounded-full mr-3 ${monthData.isFutureMonth ? 'bg-gray-300' : monthData.isPastMonth ? 'bg-gray-400' : monthData.netProfit > 0 ? 'bg-green-500' : monthData.netProfit < 0 ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                              <div>
                                <div className="text-sm font-medium text-gray-900 flex items-center">
                                  {(() => {
                                    const monthNumber = parseInt(monthData.month.split('-')[1]);
                                    const year = parseInt(monthData.month.split('-')[0]);
                                    const monthNames = [
                                      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                                      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
                                    ];
                                    return `${monthNames[monthNumber - 1]} ${year}`;
                                  })()}
                                  {monthData.isCurrentMonth && (
                                    <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                      ✓ Aktuell
                                    </span>
                                  )}
                                  {monthData.isFutureMonth && (
                                    <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                                      ⏳ Zukunft
                                    </span>
                                  )}
                                  {monthData.isPastMonth && (
                                    <span className="ml-2 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-full">
                                      📅 Vergangen
                                    </span>
                                  )}
                                </div>
                                {(monthData.revenue > 0 || monthData.netProfit !== 0) && !monthData.isFutureMonth && (
                                  <div className="text-xs text-gray-500">Daten verfügbar</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                            {formatCurrency(monthData.revenue)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-red-600">
                            -{formatCurrency(monthData.costs)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-green-600">
                            +{formatCurrency(monthData.fixedRevenue)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-red-600">
                            -{formatCurrency(monthData.fixedCosts)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <span className={monthData.manualPayments >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {monthData.manualPayments >= 0 ? '+' : ''}
                              {formatCurrency(monthData.manualPayments)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-bold">
                            <span className={monthData.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {formatCurrency(monthData.netProfit)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <span className={monthData.netMargin >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {monthData.netMargin.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Automaten-spezifische Budget-Übersicht */}
            {automaten.length > 0 && (
              <div className="bg-white border rounded-lg overflow-hidden mb-8">
                <div className="px-6 sm:px-6 py-4 border-b border-gray-200 bg-blue-50">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
                    <i className="ri-robot-line mr-2 text-blue-600"></i>
                    Automaten Budget-Übersicht {new Date().getFullYear()}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">Monatliche Finanzaufschlüsselung pro Automat mit Umsatzbeteiligung und fixen Einnahmen</p>
                </div>
                
                <div className="p-6 space-y-8">
                  {automaten.map(automat => (
                    <div key={automat.id} className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                      <div className="px-4 py-3 bg-gray-100 border-b border-gray-200">
                        <h4 className="text-sm sm:text-lg font-medium text-gray-900 flex items-center">
                          <i className="ri-robot-line mr-2 text-blue-600"></i>
                          {automat.name}
                          <span className="ml-2 text-xs sm:text-sm text-gray-500">
                            ({automat.location})
                          </span>
                        </h4>
                      </div>
                      
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Monat</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Umsatz</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Wareneinsatz</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fixe Einnahmen</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fixkosten</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Umsatzbeteiligung</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Nettogewinn</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Marge</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {(() => {
                              const currentYear = new Date().getFullYear();
                              const automatResults = [];

                              // Generate data for all 12 months for this automat
                              for (let month = 1; month <= 12; month++) {
                                const automatData = calculateAutomatMonthlyData(automat, currentYear, month);
                                automatResults.push(automatData);
                              }

                              return automatResults.map((monthData, index) => (
                                <tr key={monthData.month} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${monthData.isCurrentMonth ? 'bg-blue-50' : ''} ${monthData.isFutureMonth ? 'opacity-60' : ''}`}>
                                  <td className="px-4 py-2 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <div className={`w-2 h-2 rounded-full mr-2 ${monthData.isFutureMonth ? 'bg-gray-300' : monthData.netProfit > 0 ? 'bg-green-500' : monthData.netProfit < 0 ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                                      <div className="text-sm text-gray-900">
                                        {(() => {
                                          const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
                                            'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
                                          return `${monthNames[monthData.month - 1]} ${new Date().getFullYear()}`;
                                        })()}
                                        {monthData.isCurrentMonth && <span className="ml-1 text-xs text-blue-600">●</span>}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-right text-sm text-gray-700">{formatCurrency(monthData.revenue)}</td>
                                  <td className="px-4 py-2 whitespace-nowrap text-right text-sm text-red-600">-{formatCurrency(monthData.costs)}</td>
                                  <td className="px-4 py-2 whitespace-nowrap text-right text-sm text-green-600">+{formatCurrency(monthData.fixedRevenue)}</td>
                                  <td className="px-4 py-2 whitespace-nowrap text-right text-sm text-red-600">-{formatCurrency(monthData.fixedCosts)}</td>
                                  <td className="px-4 py-2 whitespace-nowrap text-right text-sm text-orange-600">-{formatCurrency(monthData.revenueShare)}</td>
                                  <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-bold">
                                    <span className={monthData.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                                      {formatCurrency(monthData.netProfit)}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-right text-sm">
                                    <span className={monthData.netMargin >= 0 ? 'text-green-600' : 'text-red-600'}>
                                      {monthData.netMargin.toFixed(1)}%
                                    </span>
                                  </td>
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Summary Cards */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Beste Monate</p>
                    <p className="text-xl font-bold text-green-700">{(() => {
                      const currentYear = new Date().getFullYear();
                      const currentMonth = new Date().getMonth() + 1;
                      let profitableMonths = 0;

                      for (let month = 1; month <= currentMonth; month++) {
                        const monthData = calculateMonthlyData(currentYear, month);
                        if (monthData.netProfit > 0) {
                          profitableMonths++;
                        }
                      }

                      return `${profitableMonths}/${currentMonth}`;
                    })()}</p>
                  </div>
                  <i className="ri-trophy-line text-2xl text-green-600"></i>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">Aktueller Monat</p>
                    <p className="text-xl font-bold text-blue-600">
                      {new Date().toLocaleDateString('de-DE', { month: 'short' })}
                    </p>
                  </div>
                  <i className="ri-calendar-line text-2xl text-blue-600"></i>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600">Automaten aktiv</p>
                    <p className="text-xl font-bold text-purple-600">
                      {automaten.length}
                    </p>
                  </div>
                  <i className="ri-robot-line text-2xl text-purple-600"></i>
                </div>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-800">Verbleibt {new Date().getFullYear()}</p>
                    <p className="text-xl font-bold text-orange-600">
                      {12 - new Date().getMonth()} Monate
                    </p>
                  </div>
                  <i className="ri-time-line text-2xl text-orange-600"></i>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <i className="ri-information-line text-blue-600 text-xl mr-3 mt-0.5"></i>
                <div>
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Automatische Monatserkennung</h4>
                  <div className="text-sm text-blue-800 space-y-1">
                    <p>• <strong>✓ Aktuell:</strong> Der vom System automatisch erkannte aktuelle Monat</p>
                    <p>• <strong>📅 Vergangen:</strong> Vergangene Monate mit verfügbaren Daten</p>
                    <p>• <strong>⏳ Zukunft:</strong> Zukünftige Monate ohne Daten (ausgegraut)</p>
                    <p>• <strong>Smart-Update:</strong> Das System erkennt automatisch Monatswechsel und aktualisiert die Anzeige</p>
                    <p>• <strong>Automaten-Details:</strong> Jeder Automat zeigt separate Finanzaufschlüsselung mit Umsatzbeteiligung</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
